import { CommonActions, DrawerActions } from '@react-navigation/native';
import { Link, useRootNavigation } from 'expo-router';
import { StyleSheet } from 'react-native';

import {
  Button,
  Favicon,
  FaviconWhite,
  HeaderFrame,
  HeaderIconButton,
  IconMenu,
  type IconProps,
  XStack,
} from '@fhs/ui';
import { useClickLogs } from '@fhs-legacy/frontend/src/hooks/use-click-logs';
import { useAuthContext } from '@fhs-legacy/frontend/src/state/auth';

import { HeaderLeftMobile } from './header-left-mobile';
import { ScanButton } from './scan-button';
import { SignUpButton } from './sign-up-button';
import { AppHeaderProps } from './types';

function AppHeaderMiddle() {
  return (
    <Link href="/" asChild>
      <Button type="ghost" size="xl" accessibilityLabel="Go home" aria-label="Go home">
        <Favicon size={40} accessible={false} accessibilityElementsHidden />
      </Button>
    </Link>
  );
}

function AppHeaderRight({}: AppHeaderProps) {
  const navigation = useRootNavigation();

  const { isAuthenticated } = useAuthContext();
  const { logScanButtonClickEvent } = useClickLogs();
  return (
    <XStack style={styles.gap24}>
      <XStack style={styles.gap8}>
        {isAuthenticated ? (
          <ScanButton
            onPress={() => {
              logScanButtonClickEvent();
              navigation?.dispatch(CommonActions.navigate('redeem'));
            }}
          />
        ) : (
          <Link href="/signin/" asChild>
            <SignUpButton style={{ maxWidth: 150, paddingHorizontal: 2 }} />
          </Link>
        )}
      </XStack>
    </XStack>
  );
}

function AppHeaderMiddleDark() {
  return (
    <Link href="/" asChild>
      <Button type="ghost" size="xl" accessibilityLabel="Go home" aria-label="Go home">
        <FaviconWhite size={40} accessible={false} accessibilityElementsHidden />
      </Button>
    </Link>
  );
}

// Wrapper that bypasses ButtonIcon's color injection so the menu icon stays white
function WhiteIconMenu({ color: _ignored, ...props }: IconProps) {
  return <IconMenu {...props} color="white" />;
}

function AppHeaderLeftDark() {
  const navigation = useRootNavigation();
  return (
    <HeaderIconButton
      accessibilityLabel="Menu"
      onPress={() => navigation?.dispatch(DrawerActions.openDrawer())}
    >
      <WhiteIconMenu aria-hidden />
    </HeaderIconButton>
  );
}

function AppHeaderRightDark() {
  const navigation = useRootNavigation();
  const { isAuthenticated } = useAuthContext();
  const { logScanButtonClickEvent } = useClickLogs();
  return (
    <XStack style={styles.gap24}>
      <XStack style={styles.gap8}>
        {isAuthenticated ? (
          <ScanButton
            type="outline-inverted"
            onPress={() => {
              logScanButtonClickEvent();
              navigation?.dispatch(CommonActions.navigate('redeem'));
            }}
          />
        ) : (
          <Link href="/signin/" asChild>
            <SignUpButton
              type="outline-inverted"
              style={{ marginHorizontal: 0, maxWidth: 150, paddingHorizontal: 8 }}
            />
          </Link>
        )}
      </XStack>
    </XStack>
  );
}

export function AppHeaderMobile({ left, middle, right, darkMode = false }: AppHeaderProps) {
  if (darkMode) {
    return (
      <HeaderFrame backgroundColor="transparent" style={styles.frame}>
        <HeaderFrame.Left>
          {left === undefined ? <AppHeaderLeftDark /> : left}
        </HeaderFrame.Left>
        <HeaderFrame.Middle>
          {middle === undefined ? <AppHeaderMiddleDark /> : middle}
        </HeaderFrame.Middle>
        <HeaderFrame.Right>
          {right === undefined ? <AppHeaderRightDark /> : right}
        </HeaderFrame.Right>
      </HeaderFrame>
    );
  }

  return (
    <HeaderFrame style={styles.frame}>
      <HeaderFrame.Left>{left ?? <HeaderLeftMobile />}</HeaderFrame.Left>
      <HeaderFrame.Middle>{middle ?? <AppHeaderMiddle />}</HeaderFrame.Middle>

      {/* there is an instance where null is given to the right to hide signup button  */}
      <HeaderFrame.Right>{right === undefined ? <AppHeaderRight /> : right}</HeaderFrame.Right>
    </HeaderFrame>
  );
}

const styles = StyleSheet.create({
  frame: {
    height: 56,
  },
  gap8: {
    gap: 30,
  },
  gap24: {
    gap: 24,
  },
});
